import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { Check, Sparkles, X } from "lucide-react";

const per = { week: "/week", month: "/month", year: "/year" };
const STAGGER_MS = 70;

export default function Subscriptions() {
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);
  const [email, setEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/subscription-plans").then((r) => setPlans(r.data)); }, []);

  const subscribe = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Please enter a valid email."); return; }
    if (!agreed) { toast.error("Please agree to our Policies to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/checkout", { plan_id: active.id, email: email.trim(), recipient_name: recipientName.trim(), origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start subscription.");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
      <div className="text-center max-w-2xl mx-auto">
        <span className="gh-overline flex items-center justify-center gap-2"><Sparkles className="h-4 w-4 text-[#D9CBA0]" /> Gift subscriptions</span>
        <h1 className="font-heading text-5xl sm:text-6xl text-[#3A3632] mt-4">Send hope, again and again.</h1>
        <div className="gh-hairline my-7 max-w-[120px] mx-auto" />
        <p className="text-lg text-[#6B655F] leading-relaxed font-light">Choose a rhythm that fits — weekly, monthly, or yearly — and we'll deliver a beautifully curated box (with a signature Hope Card) on repeat. Cancel anytime.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-14 items-start">
        {plans.map((plan, i) => (
          <div key={plan.id} data-testid={`plan-${plan.id}`}
            className={`bg-white border p-8 gh-fade-up relative ${plan.popular ? "border-[#8E9A86] shadow-[0_16px_50px_rgba(138,129,117,0.16)] md:-mt-3" : "border-[#D9CBA0]/40"}`}
            style={{ animationDelay: `${i * STAGGER_MS}ms` }}>
            {plan.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8E9A86] text-white text-[10px] tracking-[0.18em] uppercase px-3 py-1 rounded-full">Most Loved</span>}
            <h3 className="font-heading text-3xl text-[#3A3632]">{plan.name}</h3>
            <p className="text-sm text-[#8A8175] mt-1">{plan.tagline}</p>
            <div className="mt-5 flex items-end gap-1">
              <span className="font-heading text-4xl text-[#8E9A86]">{money(plan.price)}</span>
              <span className="text-[#8A8175] mb-1.5">{per[plan.interval]}</span>
            </div>
            <p className="text-sm text-[#6B655F] mt-4 leading-relaxed">{plan.desc}</p>
            <ul className="mt-5 space-y-2.5">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-[#3A3632]"><Check className="h-4 w-4 text-[#8E9A86] mt-0.5 shrink-0" />{f}</li>
              ))}
            </ul>
            <button onClick={() => { setActive(plan); setEmail(""); setAgreed(false); }} data-testid={`subscribe-${plan.id}`}
              className={`w-full mt-7 rounded-full px-6 py-3 font-medium transition-colors ${plan.popular ? "bg-[#8E9A86] text-white hover:bg-[#7A8772]" : "border border-[#8A8175] text-[#3A3632] hover:bg-[#FAF7F2]"}`}>
              Choose {plan.name}
            </button>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-[#8A8175] mt-10 max-w-xl mx-auto leading-relaxed">
        Your first box is charged today, then automatically each {plans[0]?.interval || "cycle"} to keep the encouragement flowing. We'll email you each time a gift ships, and you can pause or cancel anytime by contacting us.
      </p>

      {active && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#1A1A1A]/55 backdrop-blur-sm" data-testid="subscribe-modal" onClick={() => !busy && setActive(null)}>
          <div className="bg-[#FAF7F2] w-full max-w-md border border-[#D9CBA0]/60 shadow-2xl p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <span className="gh-overline">Start subscription</span>
                <h3 className="font-heading text-3xl text-[#3A3632] mt-1">{active.name}</h3>
              </div>
              <button onClick={() => setActive(null)} data-testid="subscribe-close" className="text-[#8A8175] hover:text-[#3A3632]"><X className="h-5 w-5" /></button>
            </div>
            <div className="gh-hairline my-5" />
            <div className="flex justify-between items-center mb-4">
              <span className="text-[#6B655F]">Billed {active.interval}ly</span>
              <span className="font-heading text-3xl text-[#8E9A86]">{money(active.price)}{per[active.interval]}</span>
            </div>
            <label className="block">
              <span className="text-sm text-[#3A3632] font-medium">Your email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="subscribe-email" placeholder="you@email.com"
                className="w-full mt-1.5 border border-[#D1CCC5] rounded-lg px-4 py-2.5 outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
            </label>
            <label className="block mt-3">
              <span className="text-sm text-[#3A3632] font-medium">Who is this gift for? <span className="text-[#8A8175] font-normal">(optional)</span></span>
              <input type="text" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} data-testid="subscribe-recipient" placeholder="e.g. Mom, Aunt Ruth, the Johnson family"
                className="w-full mt-1.5 border border-[#D1CCC5] rounded-lg px-4 py-2.5 outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
              <span className="text-xs text-[#8A8175] mt-1 block">We'll email you each time their gift ships 💛</span>
            </label>
            <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="subscribe-agree" className="mt-0.5 h-4 w-4 accent-[#8E9A86]" />
              <span className="text-[12px] text-[#6B655F] leading-relaxed">I agree to the <a href="/policies" className="underline hover:text-[#8E9A86]">Policies</a>. I'll be billed {active.interval}ly and can cancel anytime.</span>
            </label>
            <button onClick={subscribe} disabled={busy} data-testid="subscribe-confirm" className="w-full mt-5 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors disabled:opacity-60">
              {busy ? "Redirecting to secure checkout…" : `Subscribe — ${money(active.price)}${per[active.interval]}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
